import { useSelector } from "react-redux";
import logo_pdf from "../../Assets/svg/logo_pdf.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

export default function DownloadPdfNestedTable() {
  const contentRef = useRef(null);
  const layer = useSelector((state) => state.layer);
  const [data, setData] = useState({
    title: {
      layer: "",
      startDate: "",
      endDate: "",
    },
    head: [],
    body: [],
  });

  const handlePrint = useReactToPrint({
    content: () => contentRef.current,
    documentTitle: "",
    pageStyle: `
            @page { size: A3 landscape; margin: 4mm 4mm 10mm 4mm}
            @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0 !important; padding: 0 !important}
                html, body { margin: 0 !important; padding: 0 !important; }
            }
            `,
  });

  const onClick = useCallback(() => {
    handlePrint();
  }, [handlePrint]);

  useEffect(() => {
    const {
      selected_parent_table_row_key,
      is_nested_table_filtered,
      filtered_childs_shown,
      field_parent,
      layer_id,
      geo_layer_list,
    } = layer || false;
    const parent_uuid = field_parent?.uuid;
    let geo_layer = geo_layer_list.find((e) => e?.geo_layer?._id === layer_id);
    const selected_child_fields =
      geo_layer?.geo_layer?.geojson?.features?.find(
        (layer) => layer.geo_layer_id === layer_id
      )?.child_array || [];

    if (!geo_layer?.geo_layer) return;

    geo_layer = geo_layer?.geo_layer ? geo_layer?.geo_layer : {};
    const selected_field_title_key =
      geo_layer.fields?.find((field) => field.name === "Lokasi")?.key || "";

    // Safety check: Ensure features exist before finding
    const features = geo_layer.geojson?.features || [];
    const feature =
      features.find((item) => item.key === selected_parent_table_row_key) || {};

    const location = feature.properties?.[selected_field_title_key] || "";
    const feature_id = feature._id || "-";

    let selected_fields = geo_layer?.fields?.filter(
      (item) => item?.parent_uuid === parent_uuid
    );
    selected_fields = selected_fields.filter(
      (d) =>
        ![
          "icon_image",
          "text_fields",
          "fill_color",
          "circle_radius",
          "circle_stroke_width",
          "circle_stroke_color",
          "is_using_icon",
          "is_using_label",
        ].includes(d?.key)
    );

    let newData;
    if (is_nested_table_filtered) {
      newData = filtered_childs_shown.map((field) => field.properties);
    } else {
      newData = selected_child_fields.map((field) => field.properties);
    }

    // Fix: Logic error in original code where (name + location) was evaluated as boolean
    const layerName = geo_layer?.name || "";
    const title = location
      ? `${layerName} - Lokasi : ${location}`
      : `${layerName} - Id : ${feature_id}`;

    setData((prev) => ({ ...prev, title: { ...prev.title, layer: title } }));

    const schema = [
      ...selected_fields?.map((d) => ({
        column: d?.name,
        type: d?.type,
        value: (data) => data?.[d.key],
      })),
    ];

    const filteredFields = selected_fields?.filter(
      (field) => field?.isHighlight === true
    );

    const schemaByName = Object.fromEntries(
      schema?.map((s) => [String(s?.column).trim(), s])
    );

    const filteredSchema = filteredFields
      .map((item) => schemaByName[String(item.name).trim()])
      .filter(Boolean);

    const missing = filteredFields.filter(
      (field) => !schemaByName[String(field.name).trim()]
    );
    if (missing.length) {
      console.warn("Kolomn tidak ditemukan di schema: ", missing);
      return;
    }

    const dateFormatter = new Intl.DateTimeFormat("id-ID", {
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "Asia/Jakarta",
    });

    const head = filteredSchema.map((s) => s.column);

    const body = newData.map((row) =>
      filteredSchema.map((col) => {
        let v = col.value(row);
        if (col.type === "last_edited") {
          if (v && typeof v === "string" && v.trim() !== "") {
            const newDate = new Date(v);
            // Fix: Check if date is valid before formatting
            if (!isNaN(newDate.getTime())) {
              try {
                v = dateFormatter.format(newDate);
              } catch (e) {
                console.error("Date formatting failed", e);
                v = "-";
              }
            } else {
              v = "-";
            }
          }
        }
        return { value: v == null ? "" : String(v), type: col.type };
      })
    );

    setData((prev) => ({ ...prev, head: head, body: body }));
  }, [layer]);

  // Helper for safe footer date
  const getFooterDate = () => {
    try {
      return new Date().toLocaleString("id-ID", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "Asia/Jakarta",
      });
    } catch (e) {
      return "-";
    }
  };

  return (
    <>
      <div style={{ display: "none" }}>
        <div
          ref={contentRef}
          style={{
            width: "auto",
            display: "flex",
            flexDirection: "column",
            paddingTop: "4mm",
            paddingBottom: "4mm",
            gap: 5,
          }}
        >
          <div>
            <span>{data?.title?.layer}</span>
          </div>
          <table
            border="1"
            style={{
              tableLayout: "auto",
              borderCollapse: "collapse",
              fontSize: ".55rem",
              width: "max-content",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  No.
                </th>
                {data?.head?.map((item, index) => (
                  <th
                    key={index}
                    style={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      maxWidth: "50px",
                      fontWeight: 700,
                      border: "1px solid black",
                      padding: "2px",
                    }}
                  >
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.body?.map((items, index) => (
                <tr key={index}>
                  <td
                    key={index}
                    style={{ verticalAlign: "top", textAlign: "center" }}
                  >
                    {Number(index) + 1}
                  </td>
                  {items?.map((item, index) =>
                    [
                      ".jpg",
                      ".jpeg",
                      ".png",
                      ".webp",
                      ".gif",
                      ".bmp",
                      ".svg",
                      ".avif",
                    ].some((ext) => item.value.toLowerCase().endsWith(ext)) ? (
                      <td
                        key={index}
                        style={{
                          textAlign: "center",
                          padding: "2px",
                        }}
                      >
                        <img
                          src={item.value}
                          style={{
                            maxWidth: "80px",
                            aspectRatio: "1/1",
                          }}
                          alt=""
                        />
                      </td>
                    ) : (
                      <td
                        key={index}
                        style={{
                          maxWidth: ["checklist"].includes(item.type)
                            ? "100px"
                            : "70px",
                          verticalAlign: "top",
                          textAlign: ["number", "date", "time"].includes(
                            item.type
                          )
                            ? "center"
                            : "left",
                        }}
                      >
                        {item.type === "checklist"
                          ? (() => {
                              const value = item.value.trim();
                              const values = value.split("\n");
                              const tdStyle = {
                                verticalAlign: "top",
                                padding: 0,
                                margin: 0,
                              };

                              return values.map((value, index) => (
                                <table
                                  key={index}
                                  style={{
                                    fontSize: ".45rem",
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  <tbody
                                    style={{
                                      padding: 0,
                                      margin: 0,
                                    }}
                                  >
                                    <tr>
                                      <td style={tdStyle}>-</td>
                                      <td style={tdStyle}>{value}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              ));
                            })()
                          : item.value}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* --- TAMBAHKAN FOOTER DENGAN NOMOR HALAMAN DI SINI --- */}
          <div
            style={{
              position: "fixed",
              bottom: "0",
              left: "0",
              fontSize: "8pt",
              color: "gray",
              textAlign: "center",
              zIndex: 1000,
            }}
          >
            <span>
              {data?.title?.layer} | Diunduh pada {getFooterDate()}
            </span>
          </div>
          {/* --- AKHIR FOOTER --- */}
        </div>
      </div>

      <div style={{ display: "none" }}>
        <div
          style={{
            width: "auto",
            display: "flex",
            flexDirection: "column",
            paddingTop: "4mm",
            paddingBottom: "4mm",
            gap: 5,
          }}
        ></div>
      </div>
      <button
        className="button_big button_white outline_shadow_soft"
        onClick={onClick}
      >
        <img src={logo_pdf} width={40} alt="icon download pdf" />
      </button>
    </>
  );
}
